import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import {
  AlertBannerContext,
  API_RESPONSE_CODE
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../../Graphql/utils";
import {
  educationFeeSummaryQuery,
  listOverdueEducationInvoicesQuery
} from "../../../Graphql/querries/education/billingQuery";
import {
  listLowStockItemsQuery,
  stockLedgerQuery
} from "../../../Graphql/querries/education/stockItemQuery";
import { listStockRequisitionsQuery } from "../../../Graphql/querries/education/stockRequisitionQuery";
import {
  GraphQLCodePayload,
  GraphQLData,
  unwrapGraphQLOperation
} from "./educationGraphQLUtils";

export type EducationFeeSummary = {
  totalCollected?: number;
  totalOutstanding?: number;
  feeBillCount?: number;
  overdueCount?: number;
};

export type OverdueEducationInvoice = {
  invoiceId?: string;
  invoiceNumber?: string;
  studentId?: string;
  totalAmount?: number;
  remainingAmount?: number;
  dueDate?: string;
  status?: string;
};

export type LowStockItem = {
  id?: string;
  name?: string;
  sku?: string;
  quantityOnHand?: number;
  minimumQuantity?: number;
  baseUnit?: string;
};

type EducationFeeSummaryPayload = GraphQLCodePayload & EducationFeeSummary;

type OverdueEducationInvoicesPayload = GraphQLCodePayload & {
  invoices?: OverdueEducationInvoice[];
};

type LowStockItemsPayload = GraphQLCodePayload & {
  stockItems?: LowStockItem[];
};

type StockLedgerPayload = GraphQLCodePayload & {
  movements?: {
    id?: string;
    movementType?: string;
    quantity?: number;
    notes?: string;
    createdAt?: string;
  }[];
};

type StockRequisitionsPayload = GraphQLCodePayload & {
  requisitions?: {
    id?: string;
    orderNumber?: string;
    requisitionStatus?: string;
    lines?: { stockItemName?: string; quantity?: number }[];
  }[];
};

export const useEducationFeeSummary = () => {
  const { setAlert } = useContext(AlertBannerContext);
  return useQuery({
    queryKey: ["educationFeeSummary"],
    queryFn: async (): Promise<EducationFeeSummary | null> => {
      const data = await fetchGraphQL<
        GraphQLData<EducationFeeSummaryPayload, "educationFeeSummary">,
        Record<string, never>
      >(educationFeeSummaryQuery, {}, setAlert);
      const response = unwrapGraphQLOperation(data, "educationFeeSummary");
      return response.code === API_RESPONSE_CODE.OK ? response : null;
    }
  });
};

export const useOverdueEducationInvoices = (size = 50) => {
  const { setAlert } = useContext(AlertBannerContext);
  return useQuery({
    queryKey: ["overdueEducationInvoices", size],
    queryFn: async (): Promise<OverdueEducationInvoice[]> => {
      const data = await fetchGraphQL<
        GraphQLData<
          OverdueEducationInvoicesPayload,
          "listOverdueEducationInvoices"
        >,
        { input: { size: number } }
      >(listOverdueEducationInvoicesQuery, { input: { size } }, setAlert);
      const response = unwrapGraphQLOperation(
        data,
        "listOverdueEducationInvoices"
      );
      return response.code === API_RESPONSE_CODE.OK
        ? (response.invoices ?? [])
        : [];
    }
  });
};

export const useLowStockItems = (size = 50) => {
  const { setAlert } = useContext(AlertBannerContext);
  return useQuery({
    queryKey: ["lowStockItems", size],
    queryFn: async (): Promise<LowStockItem[]> => {
      const data = await fetchGraphQL<
        GraphQLData<LowStockItemsPayload, "listLowStockItems">,
        { input: { size: number } }
      >(listLowStockItemsQuery, { input: { size } }, setAlert);
      const response = unwrapGraphQLOperation(data, "listLowStockItems");
      return response.code === API_RESPONSE_CODE.OK
        ? (response.stockItems ?? [])
        : [];
    }
  });
};

export const useStockConsumptionLedger = (stockItemId?: string) => {
  const { setAlert } = useContext(AlertBannerContext);
  return useQuery({
    queryKey: ["stockConsumptionLedger", stockItemId ?? "all"],
    enabled: Boolean(stockItemId),
    queryFn: async () => {
      const data = await fetchGraphQL<
        GraphQLData<StockLedgerPayload, "stockLedger">,
        { input: { stockItemId?: string } }
      >(stockLedgerQuery, { input: { stockItemId } }, setAlert);
      const response = unwrapGraphQLOperation(data, "stockLedger");
      if (response.code !== API_RESPONSE_CODE.OK) return [];
      return (response.movements ?? []).filter(
        (m) => m.movementType === "STOCK_OUT"
      );
    }
  });
};

export const useIssuedStockRequisitions = () => {
  const { setAlert } = useContext(AlertBannerContext);
  return useQuery({
    queryKey: ["issuedStockRequisitions"],
    queryFn: async () => {
      const data = await fetchGraphQL<
        GraphQLData<StockRequisitionsPayload, "listStockRequisitions">,
        { input: { size: number } }
      >(listStockRequisitionsQuery, { input: { size: 50 } }, setAlert);
      const response = unwrapGraphQLOperation(data, "listStockRequisitions");
      if (response.code !== API_RESPONSE_CODE.OK) return [];
      return (response.requisitions ?? []).filter(
        (r) => r.requisitionStatus === "ISSUED"
      );
    }
  });
};
